<template>
  <validation-observer tag="div" ref="form">
    <validation-provider vid="name" :name="lodash.upperFirst($t('models.name'))" rules="required" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('models.name'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <b-form-input
            name="name"
            type="text"
            required
            :state="errors[0] ? false : (validated ? true : null)"
            v-model="model.name"
        ></b-form-input>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="date" :name="lodash.upperFirst($t('models.date'))" rules="required" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('models.date'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <date-picker
            name="date"
            format="YYYY-MM-DD HH:mm:ss"
            required
            :state="errors[0] ? false : (validated ? true : null)"
            v-model="model.date"
        >
        </date-picker>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="number" :name="lodash.upperFirst($t('models.number'))" rules="required|double" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('models.number'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <numeral-input
            name="number"
            required
            :state="errors[0] ? false : (validated ? true : null)"
            v-model.number="model.number"
        ></numeral-input>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="select_id" :name="lodash.upperFirst($t('models.select_id'))" rules="required|numeric" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('models.select_id'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect
            name="select_id"
            v-model.number="model.select_id"
            :options="[]">
        </multiselect>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="parent_id" :name="lodash.upperFirst($t('models.parent_id'))" rules="required|numeric" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          label-cols-md="2"
          :label="lodash.upperFirst($t('models.parent_id'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <multiselect-ajax
            name="parent_id"
            v-model.number="model.parent_id"
            :url="undefined">
        </multiselect-ajax>
      </b-form-group>
    </validation-provider>
    <validation-provider vid="description" :name="lodash.upperFirst($t('models.description'))" rules="" v-slot="{ validated, errors }">
      <b-form-group
          class="row mb-3"
          :label="lodash.upperFirst($t('models.description'))"
          :state="errors[0] ? false : (validated ? true : null)"
          :invalid-feedback="errors[0]"
      >
        <editor
          ref="editor"
          class="form-control"
          :init="editorInit"
          plugins="lists link image paste help wordcount"
          toolbar="undo redo | styleselect | fontselect | bold italic | alignleft aligncenter alignright alignjustify | outdent indent"
          v-model="model.description"
        />
      </b-form-group>
    </validation-provider>
  </validation-observer>
</template>

<script>
  import Editor from '@tinymce/tinymce-vue';
  import DatePicker from "@/components/inputs/datepicker";
  import MultiselectAjax from "@/components/inputs/vue-multiselect-ajax";
  import NumeralInput from '@/components/inputs/numeral';
  import Multiselect from "vue-multiselect";

  export default {
    components: {
      DatePicker,
      MultiselectAjax,
      Multiselect,
      NumeralInput,
      Editor
    },
    props: {
      value: Object,
      isValid: {type: Boolean, default: true},
      errors: {type: Object, default() { return {}; }},
      alerts: {type: Array, default() { return []; }},
    },
    data() {
      return {
        loading: false,
        model: {
          ...this.value
        },
        editorInit: {
          height: 512,
          image_title: true,
          automatic_uploads: true,
          file_picker_types: 'image',
          file_picker_callback: this.editorFilePickerCallback,
          mobile: {
            inline: true
          }
        }
      }
    },
    methods: {
      getValidationObserver() {
        if (this.validationObserverRef && this.$refs[this.validationObserverRef]) {
          return this.$refs[this.validationObserverRef];
        }

        return this.$refs.form || this.$refs.customer || null;
      },
      emitValue() {
        this.$emit('input', this.model)
      },
      validateAll() {
        const observer = this.getValidationObserver();
        if (!observer || typeof observer.validate !== 'function') {
          return Promise.resolve(true);
        }

        return observer.validate().then((isValid) => {
          if (isValid) this.emitValue()
          this.$emit('update:is-valid', isValid)
          this.$emit('update:form-errors', observer.errors || {})
          const invalidFeedback = this.$el.querySelector('.invalid-feedback');
          if (invalidFeedback) {
            invalidFeedback.scrollIntoView({behavior: "smooth", block: "center"});
            return false;
          }
          return isValid;
        }).catch(() => false)
      },
      editorFilePickerCallback(cb) {
        let vm = this;
        let input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.onchange = function () {
          let file = this.files[0];
          let reader = new FileReader();
          reader.onload = function () {
            let id = 'blobid' + (new Date()).getTime();
            let blobCache = vm.$refs.editor.editor.editorUpload.blobCache;
            let base64 = reader.result.split(',')[1];
            let blobInfo = blobCache.create(id, file, base64);
            blobCache.add(blobInfo);

            cb(blobInfo.blobUri(), { title: file.name });
          };
          reader.readAsDataURL(file);
        };
        input.click();
      }
    },
    mounted() {
      const observer = this.getValidationObserver();
      if (observer && typeof observer.setErrors === 'function') {
        observer.setErrors({
          ...(observer.errors || {}),
          ...this.errors
        })
      }
    },
    watch: {
      errors(value) {
        const observer = this.getValidationObserver();
        if (observer && typeof observer.setErrors === 'function') {
          observer.setErrors(value)
        }
      },
      model:{
        handler(value) {
          if (this.lodash.isEqual(this.value, value)) {
            this.$emit('model-changed', false)
          } else {
            this.$emit('model-changed', true)
          }
        },
        deep: true
      }
    }
  }
</script>
